import { DialogService } from 'aurelia-dialog';
import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject, BindingEngine, bindable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { Models } from 'models/core';
import { ErrorService } from 'services/error-service';
import { ServiceService } from 'services/service-service';
import { ServiceStationService } from 'services/service-station-service';
import { ToastService } from 'services/toast-service';
import { ServiceStationPipelineService } from './../../services/service-station-pipeline-service';
import { UserService } from './../../services/user-service';
import { ServiceDialog } from '../service-dialog/service-dialog';
import * as moment from 'moment';
import { AsyncErrorHandler } from 'lib/ui';
import { Sortable, SortableStartEvent, SortableSortEvent } from '@shopify/draggable';
import { asyncUtil } from 'lib/helpers';
import { ServiceQueueService } from 'services/service';
import { UpdateServiceQueuePipelineContentDto, UpdateServiceQueuePipelineServiceDto } from 'models/service';
import { ServiceQueueMoveDialog } from './service-queue-move-dialog';

type QueueOption = {
  targetId: string;
  targetKind: 'production' | 'queue';
  pipelineId: number;
  pipelineName: string;
  pipelineTypeName: string;
  isForQueue: boolean;
  label: string;
  order: number;
};

@autoinject
export class ServiceQueue {
  private saving: boolean = false;
  private serviceStationId: number;
  private serviceStation: Models.ServiceStation;
  private pipelines: any;
  private queuedServices: any = [];
  private preQueueTotalItems: number = 0;
  private pipelineData: any;
  private pipelineSearchInputHasFocus: boolean = false;
  private pipelineFilters: {
    searchText: string;
  } = {
    searchText: '',
  };
  private language: string;

  private draggableInProcess: boolean = false;

  private searchInputHasFocus: boolean = false;

  private pageSize: number = 30;
  @bindable currentPage: number = 1;

  private serviceStations: Array<Models.ServiceStation>;
  private openServiceAsDialog: boolean = true;

  private filters = {
    searchText: '',
    customerId: null,
    plannedDeliveryDateFrom: null,
    plannedDeliveryDateTo: null,
    hasPlannedDeliveryDate: null,
    statuses: {
      Washed: false,
      NotWashed: false,
      Repaired: false,
      NotRepaired: false,
      Tested: false,
      NotTested: false,
      Antifouled: false,
      NotAntifouled: false,
      PutOnStorage: false,
      PackedAndReady: false,
    },
  };

  private sortable: Sortable | null = null;
  private preQueueServices: Array<any>;

  constructor(
    private errorService: ErrorService,
    private eventAggregator: EventAggregator,
    private i18n: I18N,
    private router: Router,
    private serviceService: ServiceService,
    private serviceQueueService: ServiceQueueService,
    private serviceStationService: ServiceStationService,
    private pipelineService: ServiceStationPipelineService,
    private toastService: ToastService,
    private bindingEngine: BindingEngine,
    private userService: UserService,
    private dialogService: DialogService
  ) {
    this.language = this.i18n.getLocale();
  }

  protected async attached() {
    this.bindingEngine.propertyObserver(this.pipelineFilters, 'searchText').subscribe((newValue, oldValue) => {
      this.filterPipelineServices(newValue);
    });

    this.bindingEngine.propertyObserver(this.filters, 'searchText').subscribe(() => {
      this.currentPage = 1;
      this.refreshPreQueue();
    });

    this.eventAggregator.subscribe('lang-changed', () => {
      this.language = this.i18n.getLocale();
      void this.getServiceStationData();
    });

    void this.setup();
  }

  @AsyncErrorHandler
  private async setup() {
    const [user, serviceStations] = await Promise.all([this.userService.getCurrentUser(), this.serviceStationService.getAllCached()]);

    this.serviceStationId = user.ServiceStationId;
    this.serviceStations = serviceStations?.filter((x) => !x.IsDeleted).sort((a, b) => (a.Name > b.Name ? 1 : -1));

    if (!this.serviceStationId) {
      this.changeServiceStation(this.serviceStations[0]);
    } else {
      const serviceStation = this.serviceStations.find((x) => x.Id === this.serviceStationId);
      this.changeServiceStation(serviceStation);
    }
  }

  protected detached() {
    if (this.sortable) {
      this.sortable.destroy();
    }
  }

  @AsyncErrorHandler
  async getServiceStationData() {
    const [pipelines, queuedServices] = await Promise.all([
      this.pipelineService.getAll(
        '?$filter=ServiceStationId eq ' + this.serviceStationId + '&$orderby=ServiceStationPipelineType/SortIndex,SortIndex'
      ),
      this.serviceService.getQueuedServices(this.serviceStationId),
    ]);
    const pipelineData = [];

    // loop through the servicestation's available pipelines
    pipelines.forEach((pipeline: any) => {
      pipeline.Services = queuedServices.filter((x) => x.ServiceStationPipelineId === pipeline.Id && !x.IsInPipelineQueue);
      pipeline.ServicesQueued = queuedServices.filter((x) => x.ServiceStationPipelineId === pipeline.Id && x.IsInPipelineQueue);

      let pipelineType = pipelineData.find((x) => x.ServiceStationPipelineTypeId === pipeline.ServiceStationPipelineTypeId);

      if (!pipelineType) {
        pipelineType = {
          PipelineType: this.language === 'nb-NO' ? pipeline.ServiceStationPipelineType.Name : pipeline.ServiceStationPipelineType.NameEn,
          ServiceStationPipelineTypeId: pipeline.ServiceStationPipelineTypeId,
          Pipelines: [],
        };

        pipelineData.push(pipelineType);
      }

      pipelineType.Pipelines.push(pipeline);
    });

    this.pipelineData = pipelineData;

    this.queuedServices = queuedServices;

    // https://codepen.io/brettdonald/pen/zMVMjd

    await this.setupSortable();
    this.filterPipelineServices(this.pipelineFilters.searchText);
  }

  private async setupSortable() {
    // Wait for the DOM to be ready before initializing Sortable
    await asyncUtil.wait(200);

    this.sortable?.destroy?.();
    this.sortable = new Sortable(document.querySelectorAll('[sortable]'), {
      draggable: '[sortitem]',
      distance: 35,
    });

    this.sortable.on('sortable:start', (event: SortableStartEvent) => {
      if (this.draggableInProcess) {
        event.cancel();
      }
    });

    this.sortable.on('sortable:stop', async (event: SortableSortEvent) => {
      if (this.draggableInProcess) return;

      this.draggableInProcess = true;

      try {
        // Ensure DOM is updated before proceeding
        await asyncUtil.wait(200);

        const serviceId = event.dragEvent.source.children[0]?.attributes['data-id']?.value;
        if (!serviceId || isNaN(+serviceId)) {
          this.toastService.showError('Service ID is invalid. Cannot update service queue.');
          return;
        }

        const service =
          this.queuedServices.find((s) => +s.ServiceId === +serviceId) || this.preQueueServices.find((s) => +s.ServiceId === +serviceId);

        if (!service) {
          this.toastService.showError('Service not found in queue or prequeue. Cannot update service queue.');
          return;
        }

        const newContainerIsPreQueue = event.newContainer.hasAttribute('prequeue');
        const oldContainerIsPreQueue = event.oldContainer.hasAttribute('prequeue');

        if (newContainerIsPreQueue && oldContainerIsPreQueue) {
          return;
        }

        // If the service is moved to prequeue, we have to update the old pipeline, since the service is no longer in the queue.
        // The prequeue is not a queue nor sortable so we don't need to update the sort index.
        const container = newContainerIsPreQueue ? event.oldContainer : event.newContainer;
        // If this is true, the service is being moved within/from a queue (pipeline)
        const isForQueue = container.hasAttribute('queue');

        const pipelineId = +(container.id || null);

        if (!pipelineId || isNaN(pipelineId)) {
          this.toastService.showError('Service queue does not have a pipeline ID. Cannot update service queue.');
          return;
        }

        // Get all service elements (DOM elements) that is in the pipeline for the current container.
        const serviceElementsInQueue = Array.from(container.querySelectorAll('[sortitem]'));

        const dto = new UpdateServiceQueuePipelineContentDto();
        dto.PipelineId = pipelineId;
        dto.IsForQueue = isForQueue;
        dto.Services = [];

        let sortIndex = 0;
        for (const serviceElement of serviceElementsInQueue) {
          sortIndex++;
          const serviceId = +serviceElement.children[0].attributes['data-id'].value;
          if (!serviceId || isNaN(serviceId)) continue;

          const service =
            this.queuedServices.find((s) => +s.ServiceId === serviceId) || this.preQueueServices.find((s) => +s.ServiceId === serviceId);

          if (!service) continue;

          const update = new UpdateServiceQueuePipelineServiceDto();
          update.ServiceId = service.ServiceId;
          update.SortIndex = sortIndex;

          dto.Services.push(update);
        }

        await this.serviceQueueService.updateServiceQueuePipelineContent(dto.PipelineId, dto);
        this.toastService.showSuccess('service.queueupdated');

        // Refresh the queue and prequeue after updating
        await this.getServiceStationData();
        this.refreshPreQueue();
      } catch (error) {
        this.errorService.handleError(error);
        this.toastService.showError('An error occurred while updating the service queue.');
      } finally {
        this.draggableInProcess = false;
      }
    });
  }

  private currentPageChanged(newValue, oldValue) {
    if (parseInt(newValue, 10) === parseInt(oldValue, 10)) {
      return;
    }

    this.refreshPreQueue();
  }

  setPipelineSearchFocus() {
    this.pipelineSearchInputHasFocus = true;
  }

  filterPipelineServices(newvalue: string) {
    this.queuedServices.forEach((x) => {
      x._isFilteredOut = false;
    });

    const filterWithOutSpace = newvalue.replaceAll(' ', '').toLowerCase();
    const filterLower = newvalue.toLowerCase();
    const services = this.queuedServices.filter(
      (x: Models.ServiceQueueData) =>
        x.CustomerName.toLowerCase().indexOf(filterLower) == -1 &&
        x.NetIdentifier.replaceAll(' ', '').toLowerCase().indexOf(filterWithOutSpace) == -1
    );

    services.forEach((x) => {
      x._isFilteredOut = true;
    });
  }

  refreshPreQueue() {
    const includeTested = this.filters.statuses.Tested ? true : this.filters.statuses.NotTested ? false : null;
    const includeWashed = this.filters.statuses.Washed ? true : this.filters.statuses.NotWashed ? false : null;
    const includeRepaired = this.filters.statuses.Repaired ? true : this.filters.statuses.NotRepaired ? false : null;
    const includeAntifouled = this.filters.statuses.Antifouled ? true : this.filters.statuses.NotAntifouled ? false : null;

    this.serviceService
      .getPreQueuedServices(
        this.serviceStationId,
        this.filters.searchText,
        this.filters.customerId,
        this.filters.plannedDeliveryDateFrom,
        this.filters.plannedDeliveryDateTo,
        includeTested,
        includeWashed,
        includeRepaired,
        includeAntifouled,
        this.filters.statuses.PutOnStorage,
        this.filters.statuses.PackedAndReady,
        this.filters.hasPlannedDeliveryDate,
        this.pageSize,
        (this.currentPage - 1) * this.pageSize
      )
      .then((response) => {
        this.preQueueTotalItems = response.headers.get('x-total-count');
        response.text().then((responseText) => {
          if (responseText) {
            this.preQueueServices = JSON.parse(responseText);
          }
        });
      })
      .catch((err) => this.errorService.handleError(err));
  }

  @AsyncErrorHandler
  async refreshQueue() {
    const services = await this.serviceService.getQueuedServices(this.serviceStationId);
    this.pipelineData.forEach((pipelineGroup) => {
      pipelineGroup.Pipelines.forEach((pipeline) => {
        pipeline.Services = services.filter((x) => x.ServiceStationPipelineId === pipeline.Id && !x.IsInPipelineQueue);
        pipeline.ServicesQueued = services.filter((x) => x.ServiceStationPipelineId === pipeline.Id && x.IsInPipelineQueue);
      });
    });

    this.queuedServices = services;
    this.filterPipelineServices(this.pipelineFilters.searchText);
  }

  updateStatusFilter(event, field) {
    this.currentPage = 1;
    this.refreshPreQueue();
  }

  setFilterCustomerId(event) {
    if (!event) {
      this.filters.customerId = null;
    } else {
      this.filters.customerId = this.filters.customerId = event.detail.value;
    }

    this.refreshPreQueue();
  }

  closeFilter() {
    this.closeDropdown(null);
  }

  private setSearchFocus(event) {
    if (event.detail) {
      this.searchInputHasFocus = event.detail.value;
    }
    if (!this.searchInputHasFocus) {
      this.filters.searchText = null;
      this.refreshPreQueue();
    }
  }

  private closeDropdown(event) {
    this.eventAggregator.publish('dropdownClose', 1);
  }

  changeServiceStation(serviceStation) {
    this.serviceStationId = serviceStation.Id;
    this.serviceStation = serviceStation;

    this.currentPage = 1;

    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }

    this.getServiceStationData();
    this.refreshPreQueue();

    this.closeDropdown(null);
  }

  private showServiceDetails(id: number, netId: number) {
    if (this.openServiceAsDialog) {
      void this.dialogService.open({
        viewModel: ServiceDialog,
        model: { Id: id, NetId: netId },
        lock: false,
        position: () => {},
      });
    } else {
      this.router.navigateToRoute('service-detail', { Id: id, NetId: netId });
    }
  }

  private setFilterDate(dateToSet: string, event) {
    if (!event.firedBy) {
      this.filters[dateToSet] = null;
      this.refreshPreQueue();
      return;
    }

    const date = event.firedBy._d;

    if (
      date &&
      this.filters[dateToSet] &&
      moment.default(date).format('YYYY-MM-DD') == moment.default(this.filters[dateToSet]).format('YYYY-MM-DD')
    ) {
      return;
    }

    // hack because datepicker is slow
    setTimeout(() => {
      this.refreshPreQueue();
    }, 500);
  }

  private clearFilterDatepickers(param) {
    const paramFrom = param + 'From';
    const paramTo = param + 'To';
    this.filters[paramFrom] = null;
    this.filters[paramTo] = null;
    this.refreshPreQueue();
  }

  private setFilterHasPlannedDeliveryDate(value) {
    this.filters.hasPlannedDeliveryDate = value;
    this.refreshPreQueue();
    return true;
  }

  private clearPipelineSearchAndClose() {
    this.pipelineFilters.searchText = '';
    this.pipelineSearchInputHasFocus = false;
  }

  @AsyncErrorHandler
  private async openMoveToQueueDialog(service: any, event?: Event) {
    event?.stopPropagation();

    const serviceId = +(service?.ServiceId || service?.Id || 0);
    if (!serviceId) {
      this.toastService.showError('Service ID is invalid. Cannot update service queue.');
      return;
    }

    const queueOptions = this.getQueueOptions();
    if (queueOptions.length === 0) {
      this.toastService.showError('No move targets available for this service station.');
      return;
    }

    const selectedTargetId = this.getSelectedTargetId(service);

    const response = await this.dialogService.open({
      viewModel: ServiceQueueMoveDialog,
      model: {
        options: queueOptions,
        selectedTargetId,
      },
    }).whenClosed();

    if (response.wasCancelled || !response.output?.targetId) {
      return;
    }

    await this.moveService(serviceId, response.output.targetId, queueOptions);
  }

  @AsyncErrorHandler
  private async moveService(serviceId: number, targetId: string, options: QueueOption[]) {
    const sourceService =
      this.queuedServices.find((x: any) => +x.ServiceId === +serviceId) ||
      this.preQueueServices?.find((x: any) => +x.ServiceId === +serviceId);

    if (!sourceService) {
      this.toastService.showError('Service not found in queue or prequeue. Cannot update service queue.');
      return;
    }

    const target = options.find((x) => x.targetId === targetId);
    if (!target) {
      this.toastService.showError('Target is invalid. Cannot update service queue.');
      return;
    }

    const sourceTarget = this.getCurrentTargetFromService(sourceService);

    const isSameContainer =
      sourceTarget.targetKind !== 'prequeue' &&
      sourceTarget.pipelineId === target.pipelineId &&
      sourceTarget.isForQueue === target.isForQueue;

    const targetServices = this.getServicesInContainer(target.pipelineId, target.isForQueue, serviceId);
    const targetDto = this.createPipelineContentDto(target.pipelineId, target.isForQueue, targetServices, sourceService);
    await this.serviceQueueService.updateServiceQueuePipelineContent(targetDto.PipelineId, targetDto);

    if (sourceTarget.targetKind !== 'prequeue' && !isSameContainer) {
      const sourceServices = this.getServicesInContainer(sourceTarget.pipelineId, sourceTarget.isForQueue, serviceId);
      const sourceDto = this.createPipelineContentDto(sourceTarget.pipelineId, sourceTarget.isForQueue, sourceServices);
      await this.serviceQueueService.updateServiceQueuePipelineContent(sourceDto.PipelineId, sourceDto);
    }

    this.toastService.showSuccess('service.queueupdated');

    await this.getServiceStationData();
    this.refreshPreQueue();
  }

  private getServicesInContainer(pipelineId: number, isForQueue: boolean, excludeServiceId?: number) {
    return this.queuedServices
      .filter(
        (x: any) =>
          +x.ServiceStationPipelineId === +pipelineId &&
          x.IsInPipelineQueue === isForQueue &&
          (!excludeServiceId || +x.ServiceId !== +excludeServiceId)
      )
      .sort((a: any, b: any) => {
        const sortIndexA = Number.isFinite(+a?.SortIndex) ? +a.SortIndex : Number.MAX_SAFE_INTEGER;
        const sortIndexB = Number.isFinite(+b?.SortIndex) ? +b.SortIndex : Number.MAX_SAFE_INTEGER;

        if (sortIndexA === sortIndexB) {
          return (+a?.ServiceId || 0) - (+b?.ServiceId || 0);
        }

        return sortIndexA - sortIndexB;
      });
  }

  private getCurrentTargetFromService(
    service: any
  ): { targetKind: 'prequeue' } | { targetKind: 'production' | 'queue'; pipelineId: number; isForQueue: boolean } {
    if (!service?.ServiceStationPipelineId) {
      return { targetKind: 'prequeue' };
    }

    return {
      targetKind: service.IsInPipelineQueue ? 'queue' : 'production',
      pipelineId: +service.ServiceStationPipelineId,
      isForQueue: !!service.IsInPipelineQueue,
    };
  }

  private getSelectedTargetId(service: any): string | null {
    const current = this.getCurrentTargetFromService(service);

    if (current.targetKind === 'prequeue') {
      return null;
    }

    return (current.isForQueue ? 'queue:' : 'production:') + current.pipelineId;
  }

  private createPipelineContentDto(pipelineId: number, isForQueue: boolean, services: any[], serviceToAppend?: any) {
    const dto = new UpdateServiceQueuePipelineContentDto();
    dto.PipelineId = pipelineId;
    dto.IsForQueue = isForQueue;
    dto.Services = [];

    let sortIndex = 0;

    services.forEach((service) => {
      const serviceId = +service?.ServiceId;
      if (!serviceId) {
        return;
      }

      sortIndex++;
      const update = new UpdateServiceQueuePipelineServiceDto();
      update.ServiceId = serviceId;
      update.SortIndex = sortIndex;
      dto.Services.push(update);
    });

    if (serviceToAppend) {
      const serviceId = +serviceToAppend?.ServiceId;
      if (serviceId) {
        sortIndex++;
        const update = new UpdateServiceQueuePipelineServiceDto();
        update.ServiceId = serviceId;
        update.SortIndex = sortIndex;
        dto.Services.push(update);
      }
    }

    return dto;
  }

  private getQueueOptions(): QueueOption[] {
    const options: QueueOption[] = [];
    let order = 0;

    this.pipelineData?.forEach((pipelineGroup: any) => {
      pipelineGroup?.Pipelines?.forEach((pipeline: any) => {
        order++;
        options.push({
          targetId: 'production:' + pipeline.Id,
          targetKind: 'production',
          pipelineId: pipeline.Id,
          pipelineName: pipeline.Name,
          pipelineTypeName: pipelineGroup.PipelineType,
          isForQueue: false,
          label: pipelineGroup.PipelineType + ' - ' + pipeline.Name,
          order,
        });

        order++;
        options.push({
          targetId: 'queue:' + pipeline.Id,
          targetKind: 'queue',
          pipelineId: pipeline.Id,
          pipelineName: pipeline.Name,
          pipelineTypeName: pipelineGroup.PipelineType,
          isForQueue: true,
          label: pipelineGroup.PipelineType + ' - ' + pipeline.Name + ' ' + '(' + this.i18n.tr('general.queue').toLowerCase() + ')',
          order,
        });
      });
    });

    return options;
  }

  
}
